import React from 'react';
import ReactDOM from 'react-dom/client';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, getRedirectResult } from 'firebase/auth';
import { Authentication } from 'gcip-iap';
import { App } from './App';
import { BomboraAuthHandler } from './auth-handler';
import { validateConfig, config } from './config';

// Validate configuration on startup
validateConfig();

/**
 * Check for Auth0 deny error in URL parameters.
 * When Auth0 Action calls api.access.deny(), it redirects back with:
 * ?error=access_denied&error_description=...
 */
function checkForAuth0DenyError(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  const error = urlParams.get('error');
  const errorDescription = urlParams.get('error_description') || '';

  if (error === 'access_denied') {
    console.log('Auth0 denied access:', errorDescription);
    window.location.href = config.urls.noAccessUrl;
    return true;
  }
  return false;
}

/**
 * Check for blocking function errors before gcip-iap processes the redirect.
 * This intercepts permission-denied errors and redirects to 403 page.
 */
async function checkForBlockingFunctionError(): Promise<boolean> {
  // Only check if we're returning from a redirect (has state in URL)
  const urlParams = new URLSearchParams(window.location.search);
  const hasState = urlParams.has('state') || window.location.hash.includes('state=');

  if (!hasState) {
    return false; // Not a redirect return, continue normally
  }

  try {
    // Initialize Firebase to check redirect result (reuse existing app if already initialized)
    const app = getApps().length === 0
      ? initializeApp({
          apiKey: config.firebase.apiKey,
          authDomain: config.firebase.authDomain,
          projectId: config.firebase.projectId,
        })
      : getApp();

    const auth = getAuth(app);
    auth.tenantId = config.tenantId;

    // Check for redirect error
    await getRedirectResult(auth);

    // No error, continue with normal flow
    return false;
  } catch (error: unknown) {
    console.log('Caught redirect error:', error);

    const errorObj = error as { code?: string; message?: string };
    const code = errorObj.code || '';
    const message = errorObj.message || '';

    // Check if this is a permission/access denied error
    // Note: Auth0's api.access.deny() returns as 'auth/user-cancelled' through Firebase
    if (code.includes('blocking-function') ||
        code.includes('permission-denied') ||
        code.includes('user-cancelled') ||
        message.includes('blocking-function') ||
        message.includes('permission-denied') ||
        message.includes('PERMISSION_DENIED') ||
        message.includes('do not have access') ||
        message.includes('IdP denied access')) {
      console.log('Access denied, redirecting to 403');
      window.location.href = config.urls.noAccessUrl;
      return true; // Error handled, don't continue
    }

    // For other errors, let gcip-iap handle them
    console.log('Non-permission error, letting gcip-iap handle it');
    return false;
  }
}

// Main initialization
async function init() {
  // Check for Auth0 deny error first (before any Firebase processing)
  if (checkForAuth0DenyError()) {
    return; // Already redirecting to 403
  }

  // Skip auth flow in development mode (no IAP parameters)
  const urlParams = new URLSearchParams(window.location.search);
  const isDev = import.meta.env.DEV && !urlParams.has('apiKey');

  if (isDev) {
    console.log('Development mode: skipping auth flow for preview');
    return; // Just show the UI
  }

  // Check for blocking function errors
  const errorHandled = await checkForBlockingFunctionError();
  if (errorHandled) {
    return; // Already redirecting to 403
  }

  // Initialize the authentication handler
  const authHandler = new BomboraAuthHandler();

  // Initialize the gcip-iap Authentication instance
  // This handles all communication between GCIP and IAP
  const ciapAuth = new Authentication(authHandler);

  // Start the authentication flow
  // This will automatically handle:
  // - Checking if user is already authenticated
  // - Redirecting to sign-in if needed
  // - Processing the redirect result after sign-in
  // - Redirecting back to the IAP-protected resource
  ciapAuth.start();
}

init();

// Render the React app for any UI needs
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
